/**
 * Keeps a signed-in user off the sign-in screen, and routes a `paused` boot (a hint exists, so a
 * refresh cookie is presumed present) to the one-click continuation instead of the full password
 * form — that branch is here, not duplicated at every route that might land a paused user back
 * here via `from`.
 *
 * Deliberately no layout of its own beyond that: it used to centre the outlet in a 400px column
 * on a sunken ground, which forced every public screen into the same floating-card shape. The
 * screens own their own composition now — sign-in is a full-bleed two-zone split.
 */

import { Navigate, Outlet, useLocation } from 'react-router';

import { selectStatus } from '@pos/shared';

import { useAppSelector } from '@/app/hooks';
import { ContinueSessionPage } from '@/pages/ContinueSessionPage';

type LocationState = { from?: string } | null;

export function PublicOnlyRoute() {
  const status = useAppSelector(selectStatus);
  const location = useLocation();

  if (status === 'active') {
    // `/` rather than a named screen: `LandingRedirect` owns where a signed-in user belongs, and
    // duplicating that decision here is how the two drift apart.
    const from = (location.state as LocationState)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  if (status === 'paused') return <ContinueSessionPage />;

  return <Outlet />;
}
